import { ShapeSvg } from "@cva/renderer";
import {
  type BrandTokens,
  brandColor,
  createShapeElement,
  docMmToPx,
  type Gradient,
  insertElements,
  PATTERN_KINDS,
  PATTERN_LABELS,
  type PatternKind,
  pageSize,
  parseHex,
  type SceneDocument,
  type ShapeElement,
  toHex,
} from "@cva/shared";
import { useId, useMemo } from "react";
import { ColorButton } from "./ColorPicker";
import { useEditor, useEditorApi } from "./store";
import { Row, Section, Slider } from "./ui";

/**
 * „Hintergründe“ (docs/04-dokumentformat.md): generated backgrounds in the
 * brand's colours – vector patterns, soft gradients and corner decoration.
 * One click places a full-bleed rectangle at the back of the page (or
 * restyles the one placed before); everything stays a normal shape element.
 */

interface Palette {
  primary: string;
  accent: string;
  light: string;
}

/** Opaque mix of two colours (print-safe tints instead of transparency). */
function mix(a: string, b: string, amount: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  const t = Math.max(0, Math.min(1, amount));
  return toHex({
    r: x.r + (y.r - x.r) * t,
    g: x.g + (y.g - x.g) * t,
    b: x.b + (y.b - x.b) * t,
    a: 1,
  });
}

function palette(brand: BrandTokens | null): Palette {
  const opaque = (hex: string | undefined, fallback: string) =>
    hex ? toHex({ ...parseHex(hex), a: 1 }) : fallback;
  const primary = opaque(brand ? brandColor(brand, "primary")?.color : undefined, "#2563eb");
  const accent = opaque(brand ? brandColor(brand, "accent")?.color : undefined, "#f59e0b");
  return { primary, accent, light: mix(primary, "#ffffff", 0.9) };
}

const BACKGROUND_NAME = "Hintergrund";
const DECO_NAME = "Deko";

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The page including bleed. */
function pageBox(doc: SceneDocument, pageIndex: number): Box {
  const bleed = doc.format.print ? docMmToPx(doc, doc.format.print.bleedMm) : 0;
  const size = pageSize(doc, doc.pages[pageIndex]);
  return {
    x: -bleed,
    y: -bleed,
    width: size.width + bleed * 2,
    height: size.height + bleed * 2,
  };
}

type Preset =
  | { id: string; label: string; kind: "background"; style: (p: Palette, box: Box) => BgStyle }
  | { id: string; label: string; kind: "deco"; shapes: (p: Palette, box: Box) => ShapeElement[] };

type BgStyle = Pick<ShapeElement, "fill" | "pattern">;

const PATTERN_SIZES: Record<PatternKind, number> = {
  dots: 0.05,
  stripes: 0.07,
  grid: 0.08,
  waves: 0.08,
  zigzag: 0.07,
  circles: 0.1,
  triangles: 0.09,
  confetti: 0.09,
};

const PATTERN_PRESETS: Preset[] = PATTERN_KINDS.map((kind) => ({
  id: `pattern-${kind}`,
  label: PATTERN_LABELS[kind],
  kind: "background",
  style: (p, box) => ({
    fill: p.light,
    pattern: {
      kind,
      color: kind === "confetti" ? p.accent : mix(p.primary, "#ffffff", 0.55),
      size: Math.round(Math.min(box.width, box.height) * PATTERN_SIZES[kind]),
      rotation: kind === "stripes" ? 45 : 0,
      opacity: 1,
    },
  }),
}));

const linear = (angle: number, colors: string[]): Gradient => ({
  type: "linear",
  angle,
  stops: colors.map((color, index) => ({ offset: index / (colors.length - 1), color })),
});

const GRADIENT_PRESETS: Preset[] = [
  {
    id: "gradient-soft",
    label: "Sanft",
    kind: "background",
    style: (p) => ({ fill: linear(160, [p.light, mix(p.accent, "#ffffff", 0.75)]) }),
  },
  {
    id: "gradient-brand",
    label: "Marke",
    kind: "background",
    style: (p) => ({ fill: linear(135, [p.primary, p.accent]) }),
  },
  {
    id: "gradient-glow",
    label: "Leuchten",
    kind: "background",
    style: (p) => ({
      fill: {
        type: "radial",
        stops: [
          { offset: 0, color: mix(p.accent, "#ffffff", 0.6) },
          { offset: 0.55, color: mix(p.primary, "#ffffff", 0.5) },
          { offset: 1, color: p.primary },
        ],
      },
    }),
  },
  {
    id: "gradient-mesh",
    label: "Mesh",
    kind: "background",
    style: (p) => ({
      fill: linear(120, [
        mix(p.primary, "#ffffff", 0.7),
        mix(p.accent, "#ffffff", 0.65),
        mix(p.primary, p.accent, 0.5),
        mix(p.primary, "#ffffff", 0.35),
      ]),
    }),
  },
];

function ellipse(box: Box, cx: number, cy: number, d: number, fill: string): ShapeElement {
  const size = Math.min(box.width, box.height) * d;
  return createShapeElement("ellipse", {
    x: box.x + box.width * cx - size / 2,
    y: box.y + box.height * cy - size / 2,
    width: size,
    height: size,
    fill,
    name: DECO_NAME,
  });
}

const DECO_PRESETS: Preset[] = [
  {
    id: "deco-corners",
    label: "Kreise in Ecken",
    kind: "deco",
    shapes: (p, box) => [
      ellipse(box, 0.02, 0.02, 0.7, mix(p.primary, "#ffffff", 0.75)),
      ellipse(box, 0.98, 0.98, 0.8, mix(p.accent, "#ffffff", 0.7)),
    ],
  },
  {
    id: "deco-bubbles",
    label: "Blasen",
    kind: "deco",
    shapes: (p, box) => [
      ellipse(box, 0.92, 0.08, 0.45, mix(p.accent, "#ffffff", 0.55)),
      ellipse(box, 0.78, 0.2, 0.16, mix(p.primary, "#ffffff", 0.6)),
      ellipse(box, 0.06, 0.9, 0.5, mix(p.primary, "#ffffff", 0.75)),
      ellipse(box, 0.25, 0.97, 0.18, mix(p.accent, "#ffffff", 0.4)),
    ],
  },
  {
    id: "deco-arc",
    label: "Bogen",
    kind: "deco",
    shapes: (p, box) => [
      ellipse(box, 0.5, 1.08, 1.25, mix(p.primary, "#ffffff", 0.8)),
      ellipse(box, 0.85, 0.12, 0.22, mix(p.accent, "#ffffff", 0.5)),
    ],
  },
];

function backgroundElement(style: BgStyle, box: Box): ShapeElement {
  return createShapeElement("rect", { ...box, ...style, name: BACKGROUND_NAME });
}

/** The background placed before: the page's bottom rectangle named „Hintergrund“. */
function existingBackground(doc: SceneDocument, pageIndex: number): ShapeElement | null {
  const first = doc.pages[pageIndex]?.children[0];
  const element = first ? doc.elements[first] : undefined;
  return element?.type === "shape" && element.shape === "rect" && element.name === BACKGROUND_NAME
    ? element
    : null;
}

function Preview({ shapes, box }: { shapes: ShapeElement[]; box: Box }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const width = 64;
  const scale = width / box.width;
  return (
    <span
      className="relative block overflow-hidden rounded bg-white ring-1 ring-black/10"
      style={{ width, height: box.height * scale }}
    >
      <span
        className="absolute top-0 left-0 block origin-top-left"
        style={{ width: box.width, height: box.height, transform: `scale(${scale})` }}
      >
        {shapes.map((shape, index) => (
          <span
            key={shape.id}
            className="absolute block"
            style={{
              left: shape.x - box.x,
              top: shape.y - box.y,
              width: shape.width,
              height: shape.height,
            }}
          >
            <ShapeSvg element={shape} idPrefix={`${id}-${index}`} />
          </span>
        ))}
      </span>
    </span>
  );
}

export function BackgroundsInsert() {
  const store = useEditorApi();
  const format = useEditor((s) => s.doc.format);
  const pageWidth = useEditor((s) => pageSize(s.doc, s.doc.pages[s.pageIndex]).width);
  const pageHeight = useEditor((s) => pageSize(s.doc, s.doc.pages[s.pageIndex]).height);
  const brand = useEditor((s) => s.brand);
  const colors = useMemo(() => palette(brand), [brand]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the box depends on the format only
  const box = useMemo(
    () => pageBox(store.getState().doc, store.getState().pageIndex),
    [format, pageWidth, pageHeight, store],
  );
  // Preview shapes only change with the colours and the format (patterns are costly).
  const previews = useMemo(() => {
    const out = new Map<string, ShapeElement[]>();
    for (const preset of [...PATTERN_PRESETS, ...GRADIENT_PRESETS, ...DECO_PRESETS]) {
      out.set(
        preset.id,
        preset.kind === "background"
          ? [backgroundElement(preset.style(colors, box), box)]
          : [backgroundElement({ fill: "#ffffff" }, box), ...preset.shapes(colors, box)],
      );
    }
    return out;
  }, [colors, box]);
  const apply = (preset: Preset) => {
    const { doc: current, pageIndex } = store.getState();
    const target = pageBox(current, pageIndex);
    let selectId: string | null = null;
    store.getState().update((draft) => {
      const existing = existingBackground(draft as SceneDocument, pageIndex);
      if (preset.kind === "background") {
        const style = preset.style(colors, target);
        if (existing) {
          const element = draft.elements[existing.id] as ShapeElement;
          element.fill = style.fill;
          if (style.pattern) element.pattern = style.pattern;
          else delete element.pattern;
          selectId = existing.id;
        } else {
          const element = backgroundElement(style, target);
          insertElements(draft as SceneDocument, pageIndex, [element], 0);
          selectId = element.id;
        }
      } else {
        insertElements(
          draft as SceneDocument,
          pageIndex,
          preset.shapes(colors, target),
          existing ? 1 : 0,
        );
      }
    });
    if (selectId) store.getState().select([selectId]);
  };
  const group = (title: string, presets: Preset[]) => (
    <div className="space-y-1.5">
      <p className="text-[11px] text-zinc-500">{title}</p>
      <div className="grid grid-cols-4 gap-2">
        {presets.map((preset) => {
          const shapes = previews.get(preset.id) ?? [];
          return (
            <button
              key={preset.id}
              type="button"
              title={preset.label}
              onClick={() => apply(preset)}
              className="flex flex-col items-center gap-1 rounded-lg border border-zinc-200 p-1 text-[10px] text-zinc-600 hover:border-brand-300 hover:bg-brand-50"
            >
              <Preview shapes={shapes} box={box} />
              <span className="w-full truncate text-center">{preset.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className="space-y-2">
      {group("Muster", PATTERN_PRESETS)}
      {group("Verläufe", GRADIENT_PRESETS)}
      {group("Formen-Deko", DECO_PRESETS)}
    </div>
  );
}

/** Pattern settings of a selected shape (colour B is the shape's fill). */
export function PatternSection({ element }: { element: ShapeElement }) {
  const store = useEditorApi();
  const pattern = element.pattern;
  if (!pattern) return null;
  const set = (recipe: (target: ShapeElement) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target?.type === "shape" && target.pattern) recipe(target as ShapeElement);
      },
      merge ? { merge } : undefined,
    );
  return (
    <Section title="Muster">
      <div className="grid grid-cols-4 gap-1">
        {PATTERN_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            aria-pressed={pattern.kind === kind}
            onClick={() =>
              set((target) => {
                target.pattern!.kind = kind;
              })
            }
            className={
              pattern.kind === kind
                ? "rounded-md border border-brand-500 bg-brand-50 px-1 py-1 text-[11px] text-brand-800"
                : "rounded-md border border-zinc-200 px-1 py-1 text-[11px] text-zinc-600 hover:bg-zinc-50"
            }
          >
            {PATTERN_LABELS[kind]}
          </button>
        ))}
      </div>
      <Row>
        <span className="w-20 shrink-0 text-xs text-zinc-500">Musterfarbe</span>
        <ColorButton
          value={pattern.color}
          label="Musterfarbe"
          onChange={(color) =>
            set((target) => {
              target.pattern!.color = color;
            })
          }
        />
      </Row>
      {(
        [
          ["Größe", "size", 8, 400, pattern.size],
          ["Drehung", "rotation", -180, 180, pattern.rotation],
          ["Deckkraft", "opacity", 0, 100, Math.round(pattern.opacity * 100)],
        ] as const
      ).map(([label, field, min, max, value]) => (
        <div key={field} className="space-y-1">
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{label}</span>
            <span className="tabular-nums">{Math.round(value)}</span>
          </div>
          <Slider
            value={value}
            min={min}
            max={max}
            onChange={(v) =>
              set((target) => {
                if (field === "opacity") target.pattern!.opacity = v / 100;
                else target.pattern![field] = v;
              }, `pattern-${field}`)
            }
          />
        </div>
      ))}
      <button
        type="button"
        className="text-xs text-zinc-500 underline hover:text-zinc-800"
        onClick={() =>
          set((target) => {
            delete target.pattern;
          })
        }
      >
        Muster entfernen
      </button>
    </Section>
  );
}
